/**
 * @override
 * Enhanced ImageGallery with primary images cycler and Cylindo 3D viewer
 * - Primary images displayed as Swiper carousel with navigation arrows
 * - 1 item per view with prev/next controls
 * - Cylindo 3D viewer for complex products (when available)
 * - AR link fallback for products without 3D
 */

import React, {useMemo} from 'react';
import PropTypes from 'prop-types';

import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image';
import {Box, Skeleton as ChakraSkeleton, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils';

import {Swiper} from '@amblique/base/app/components/shared/swiper';
import CylindoARLink from '@amblique/king-living/app/components/cylindo-ar-link';
import CylindoViewer from '@amblique/king-living/app/components/cylindo-viewer';
import {useCylindoAvailability} from '@amblique/king-living/app/hooks/use-cylindo-availability';
import {shouldShowCylindo3D} from '@amblique/king-living/app/utils/product-utils';

const LARGE = 'large';

/**
 * The skeleton representation of the image gallery component. Use this component while
 * you are waiting for product data to be returnd from the server.
 */
export const Skeleton = ({size}) => {
  const styles = useMultiStyleConfig('ProductImageGallery', {size});

  return (
    <Box data-testid="sf-image-gallery-skeleton">
      <ChakraSkeleton aspectRatio={25 / 12} {...styles.heroImageSkeleton} />
    </Box>
  );
};

Skeleton.propTypes = {
  size: PropTypes.bool,
};

const ImageGallery = ({
  isSimpleProduct = false,
  lazy = false,
  product,
  selectedVariationAttributes = {},
  size,
}) => {
  if (!product) {
    return <Skeleton />;
  }

  const styles = useMultiStyleConfig('ImageGallery', {size});

  // Use processed image groups (ALWAYS populated with PIM or Cylindo images)
  // Returns: { imageGroups: [...], imageSource: 'pim'|'cylindo' }
  const {imageGroups, imageSource} = useMemo(() => {
    if (!product?.c_processedImageGroups) {
      return {imageGroups: [], imageSource: 'cylindo'};
    }

    try {
      const processedData = JSON.parse(product.c_processedImageGroups);

      // Validate structure
      if (processedData && processedData.imageGroups && Array.isArray(processedData.imageGroups)) {
        // Extract imageSource from the 'large' viewType group (used for PDP hero images)
        const largeGroup = processedData.imageGroups.find((g) => g.viewType === 'large');
        const imageSourceForLarge = largeGroup?.imageSource || 'cylindo';

        return {
          imageGroups: processedData.imageGroups,
          imageSource: imageSourceForLarge,
        };
      }
    } catch (error) {
      console.error('[ProductView] Failed to parse processed image groups:', error);
    }

    return {imageGroups: [], imageSource: 'cylindo'};
  }, [product]);

  // Get the 'hero' image group for the current variation.
  // Memoize to avoid recalculating when dependencies haven't changed
  const heroImageGroup = useMemo(() => {
    // Try to find a group matching the selected variation
    let result = findImageGroupBy(imageGroups, {
      viewType: LARGE,
      selectedVariationAttributes,
    });

    // FALLBACK: If no matching group found (variant has no images),
    // use the first "large" group without variationAttributes (usually PIM images)
    if (!result) {
      result = imageGroups?.find((g) => g.viewType === LARGE && !g.variationAttributes);
    }

    return result;
  }, [imageGroups, selectedVariationAttributes]);

  // Check if we should show Cylindo 3D viewer (use actualImageSource from the group)
  const shouldShow3D = shouldShowCylindo3D(product, imageSource);

  // Get Cylindo availability (only for complex products with Cylindo images)
  const {has3D, hasAR, cylindoParams, isLoading} = useCylindoAvailability(
    shouldShow3D ? product : null,
    imageGroups,
    imageSource
  );

  const loadingStrategy = lazy ? 'lazy' : 'eager';

  // Memoize widths object to avoid creating new object on every render
  const widths = useMemo(() => ({
    base: '100vw',
    lg: styles.heroImage.maxWidth[3],
  }), [styles.heroImage.maxWidth[3]]);

  // Swiper options with stable callback reference
  const heroSwiperOptions = useMemo(() => {
    let slideUpdateTimer = 0;

    return {
      onSlidesUpdated(swiper) {
        clearTimeout(slideUpdateTimer);

        slideUpdateTimer = setTimeout(() => {
          // Does the same as `.slideTo` but for the case when used with enabled loop.
          // So this method will slide to slides with `realIndex` matching to passed index.
          swiper.slideToLoop(0);
        }, 10);
      },
    };
  }, []);

  // RENDERING LOGIC:
  // 1. Complex products with Cylindo images AND 3D available: Show 3D viewer only
  // 2. All other cases: Show swiper (+ AR link if available for Cylindo products)

  return isLoading ? null : (
    has3D && cylindoParams ? (
      <CylindoViewer
        code={cylindoParams.productCode}
        configuration={cylindoParams.configuration}
        customerId={cylindoParams.customerId}
      />
    ) : (
      heroImageGroup?.images?.length > 0 && (
        <Box>
          <Swiper options={heroSwiperOptions} presets="product_gallery">
            {heroImageGroup?.images?.map((image, index) => (
              <DynamicImage
                key={index}
                src={image.disBaseLink || image.link}
                widths={widths}
                imageProps={{
                  alt: image.alt || `Product image ${index + 1}`,
                  loading: index === 0 ? loadingStrategy : 'lazy',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ))}
          </Swiper>

          {/* Show AR link if available (but 3D is not) for complex Cylindo products */}
          {hasAR && cylindoParams && !isSimpleProduct && imageSource === 'cylindo' && (
            <CylindoARLink
              arqrUrl={cylindoParams.arqrUrl}
              productName={product?.name}
            />
          )}
        </Box>
      )
    )
  );
};

ImageGallery.propTypes = {
  /**
   * Whether this is a simple product (finished good)
   */
  isSimpleProduct: PropTypes.bool,

  /**
   * Determines whether the image will be lazy loaded or not
   */
  lazy: PropTypes.bool,

  /**
   * Product object (needed for Cylindo components)
   */
  product: PropTypes.object,

  /**
   * The current selected variation values
   */
  selectedVariationAttributes: PropTypes.object,

  /**
   * Size of the Image gallery, this will be used to determined the max width from styles
   */
  size: PropTypes.string,
};

export default ImageGallery;
